/**
 * adminRoutes.tsx — platform-admin-only operator routes
 * Extracted from App.tsx in #2470 (pure refactor, no behaviour changes).
 *
 * Covers: /admin/hives/:name/billing, /admin/users/:sub,
 *         /admin/cost-dashboard, /admin/deploy-status, /admin/deploys,
 *         /admin/support, /audit
 */
import React, { lazy, Suspense } from "react";
import { Route } from "react-router-dom";
import type { PageFallbackProps, PlatformAdminProtectedComponent } from "./types";

// Lazy-loaded admin pages
const AdminHiveBilling    = lazy(() => import("../pages/AdminHiveBilling").then(m => ({ default: m.AdminHiveBilling })));
// #2256 — operator user detail + onboarding reset
const AdminUserDetail     = lazy(() => import("../pages/AdminUserDetail").then(m => ({ default: m.AdminUserDetail })));
const AdminCostDashboard  = lazy(() => import("../pages/AdminCostDashboard").then(m => ({ default: m.AdminCostDashboard })));
// Deploy status — #2231 + #2243 consolidated
const AdminDeployStatus   = lazy(() => import("../pages/AdminDeployStatus").then(m => ({ default: m.AdminDeployStatus })));
const AdminAudit          = lazy(() => import("../pages/AdminAudit").then(m => ({ default: m.AdminAudit })));
// #2444 — support@2hives.ai ticket inbox (founder triage)
const AdminSupportInbox   = lazy(() => import("../pages/AdminSupportInbox").then(m => ({ default: m.AdminSupportInbox })));
const AdminAccount        = lazy(() => import("../pages/AdminAccount").then(m => ({ default: m.AdminAccount })));
// #2311 — unified incident timeline
const AdminIncidents      = lazy(() => import("../pages/AdminIncidents").then(m => ({ default: m.AdminIncidents })));
// #3016 -- consolidated founder action/decision queue with inline action recipes
const AdminFounderTodo    = lazy(() => import("../pages/AdminFounderTodo").then(m => ({ default: m.AdminFounderTodo })));

interface Props {
  PageFallback: React.ComponentType<PageFallbackProps>;
  PlatformAdminProtected: PlatformAdminProtectedComponent;
}

/** Returns the array of platform-admin-only <Route> elements. */
export function getAdminRoutes({ PageFallback, PlatformAdminProtected }: Props): React.ReactElement[] {
  const F = <PageFallback />;
  const P = PlatformAdminProtected;

  return [
    // Per-hive billing operator UI — platform-admin only (#2331)
    <Route key="admin-hive-billing"  path="/admin/hives/:name/billing" element={<P><Suspense fallback={F}><AdminHiveBilling /></Suspense></P>} />,
    // #2256 — operator user detail + onboarding reset
    <Route key="admin-user-detail"   path="/admin/users/:sub"          element={<P><Suspense fallback={F}><AdminUserDetail /></Suspense></P>} />,
    // Per-hive cost dashboard — platform-admin only (#2229)
    <Route key="admin-cost"          path="/admin/cost-dashboard"      element={<P><Suspense fallback={F}><AdminCostDashboard /></Suspense></P>} />,
    // Deploy status page — platform-admin only (#2231 + #2243 consolidated)
    <Route key="admin-deploy-status" path="/admin/deploy-status"       element={<P><Suspense fallback={F}><AdminDeployStatus /></Suspense></P>} />,
    // #2243 alias — /admin/deploys renders the same consolidated page
    <Route key="admin-deploys"       path="/admin/deploys"             element={<P><Suspense fallback={F}><AdminDeployStatus /></Suspense></P>} />,
    // #2444 — support@2hives.ai ticket inbox — founder triage view
    <Route key="admin-support"       path="/admin/support"             element={<P><Suspense fallback={F}><AdminSupportInbox /></Suspense></P>} />,
    // #2425 — /audit is platform-admin only; backend guards data independently
    <Route key="audit"               path="/audit"                     element={<P><Suspense fallback={F}><AdminAudit /></Suspense></P>} />,
    // #2311 — unified incident timeline (deploy fails + token alerts + drift + smoke + SES + CI)
    <Route key="admin-incidents"     path="/admin/incidents"           element={<P><Suspense fallback={F}><AdminIncidents /></Suspense></P>} />,
    // #3016 -- consolidated founder action/decision queue with inline action recipes
    <Route key="admin-founder-todo"  path="/admin/founder-todo"        element={<P><Suspense fallback={F}><AdminFounderTodo /></Suspense></P>} />,
  ];
}
